import { Filter, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FILTER_ANY_VALUE,
  filterFieldLabelClasses,
  filterGridClasses,
  filterInputClasses,
  filterSearchButtonClasses,
  filterSelectTriggerClasses,
  filterSubmitButtonClasses,
} from '@/lib/filters';
import { cn } from '@/lib/utils';

import { CatalogFilterCombobox, type ComboboxOption } from './CatalogFilterCombobox';
import {
  isExclusionMode,
  type CountryOption,
  type ExclusionFilterValues,
  type ExclusionStatus,
  type MethodOption,
} from './lib';

type Props = {
  values: ExclusionFilterValues;
  onApply: (values: ExclusionFilterValues) => void;
  methodOptions: MethodOption[];
  countryOptions: CountryOption[];
  collectionOptions: ComboboxOption[];
  collectionsTruncated: boolean;
};

/**
 * One filter bar for the whole page, laid out like the shipments one: labelled
 * fields four to a row and a single apply button.
 *
 * Everything is staged in a local draft and committed together. The catalog-side
 * fields used to apply on change while the exclusion-side ones waited for a button,
 * which meant two Search buttons and two different mental models on one screen.
 */
export function ExclusionFilters({
  values,
  onApply,
  methodOptions,
  countryOptions,
  collectionOptions,
  collectionsTruncated,
}: Props) {
  const { t } = useTranslation('excluded-products');
  const [draft, setDraft] = useState<ExclusionFilterValues>(values);

  const set = <K extends keyof ExclusionFilterValues>(key: K, value: ExclusionFilterValues[K]) =>
    setDraft((previous) => ({ ...previous, [key]: value }));

  // Previewed from the draft, not the applied values, so picking a service or
  // status shows immediately that collection and tag are about to stop applying.
  const catalogFiltersDisabled = isExclusionMode(draft);

  const apply = () => onApply(draft);

  return (
    <div className="flex flex-col gap-[29px]">
      <div className={filterGridClasses}>
        <div className="flex flex-col gap-3">
          <span className={filterFieldLabelClasses}>{t('filters.productLabel')}</span>
          <div className="flex">
            <input
              value={draft.search}
              onChange={(event) => set('search', event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') apply();
              }}
              placeholder={t('filters.productPlaceholder')}
              aria-label={t('filters.productLabel')}
              className={filterInputClasses}
            />
            <button
              type="button"
              onClick={apply}
              aria-label={t('filters.search')}
              className={filterSearchButtonClasses}
            >
              <Search className="size-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <span className={filterFieldLabelClasses}>{t('filters.collectionLabel')}</span>
          <CatalogFilterCombobox
            options={collectionOptions}
            value={draft.collectionId}
            onChange={(value) => set('collectionId', value)}
            anyLabel={t('catalogFilters.anyCollection')}
            searchPlaceholder={t('catalogFilters.search')}
            emptyLabel={t('catalogFilters.noResults')}
            truncatedLabel={collectionsTruncated ? t('catalogFilters.truncated') : undefined}
            disabled={catalogFiltersDisabled}
            ariaLabel={t('filters.collectionLabel')}
          />
        </div>

        <div className="flex flex-col gap-3">
          <span className={filterFieldLabelClasses}>{t('filters.tagLabel')}</span>
          {/*
            Free text, not a picker: a shop can carry thousands of tags, so any
            pre-loaded list would be either enormous or misleadingly truncated.
            Shopify matches the tag exactly, so this is not a partial search.
          */}
          <input
            value={draft.tag}
            onChange={(event) => set('tag', event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') apply();
            }}
            disabled={catalogFiltersDisabled}
            placeholder={t('filters.tagPlaceholder')}
            aria-label={t('filters.tagLabel')}
            className={cn(
              filterInputClasses,
              'rounded-[10px] border-r',
              catalogFiltersDisabled && 'cursor-not-allowed opacity-50',
            )}
          />
        </div>

        <div className="flex flex-col gap-3">
          <span className={filterFieldLabelClasses}>{t('filters.showLabel')}</span>
          <Select
            value={draft.show}
            onValueChange={(value) => set('show', value === 'excluded' ? 'excluded' : 'all')}
          >
            <SelectTrigger className={filterSelectTriggerClasses}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.showAll')}</SelectItem>
              <SelectItem value="excluded">{t('filters.showExcluded')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-3">
          <span className={filterFieldLabelClasses}>{t('filters.methodsLabel')}</span>
          <Select
            value={draft.methodKey ?? FILTER_ANY_VALUE}
            onValueChange={(value) => set('methodKey', value === FILTER_ANY_VALUE ? null : value)}
          >
            <SelectTrigger className={filterSelectTriggerClasses}>
              <SelectValue placeholder={t('filters.methodsPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY_VALUE}>{t('filters.all')}</SelectItem>
              {methodOptions.map((option) => (
                <SelectItem key={option.methodKey} value={option.methodKey}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-3">
          <span className={filterFieldLabelClasses}>{t('filters.marketsLabel')}</span>
          <Select
            value={draft.country ?? FILTER_ANY_VALUE}
            onValueChange={(value) => set('country', value === FILTER_ANY_VALUE ? null : value)}
          >
            <SelectTrigger className={filterSelectTriggerClasses}>
              <SelectValue placeholder={t('filters.marketsPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY_VALUE}>{t('filters.all')}</SelectItem>
              {countryOptions.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-3">
          <span className={filterFieldLabelClasses}>{t('filters.statusLabel')}</span>
          <Select
            value={draft.status ?? FILTER_ANY_VALUE}
            onValueChange={(value) =>
              set('status', value === FILTER_ANY_VALUE ? null : (value as ExclusionStatus))
            }
          >
            <SelectTrigger className={filterSelectTriggerClasses}>
              <SelectValue placeholder={t('filters.statusPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY_VALUE}>{t('filters.all')}</SelectItem>
              <SelectItem value="active">{t('status.active')}</SelectItem>
              <SelectItem value="disabled">{t('status.disabled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        className={cn(
          'flex items-center gap-4',
          catalogFiltersDisabled ? 'justify-between' : 'justify-end',
        )}
      >
        {catalogFiltersDisabled ? (
          <p className="text-sm leading-5 text-muted-foreground">
            {t('catalogFilters.unavailableInExclusionMode')}
          </p>
        ) : null}

        <button type="button" onClick={apply} className={filterSubmitButtonClasses}>
          <Filter className="size-4" />
          {t('filters.search')}
        </button>
      </div>
    </div>
  );
}
